import { CSSObject } from "@emotion/react";
import { List, ListItemButton, styled, Theme } from "@mui/material";
import MuiDrawer from "@mui/material/Drawer";

interface ListButtonProps {
    isActive: boolean;
}

interface HeaderProps {
    isOpen: boolean;
}

const SIDEBAR_WIDTH = 200;
const SIDEBAR_MARGIN = 112;

const openedMixin = (theme: Theme): CSSObject => ({
    marginTop: SIDEBAR_MARGIN,
    width: SIDEBAR_WIDTH,
    padding: "0 12px",
    transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: "hidden",

    "& .MuiList-root": {
        "& .MuiListSubheader-root, .MuiTypography-root": {
            display: "block",
            fontSize: "14px",
            fontFamily: "Figtree",
        },
        "& .MuiButtonBase-root": {
            padding: "5px 12px",
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "8px",

            "& .MuiListItemIcon-root": {
                minWidth: "unset",
            },
        },
    },
});

const closedMixin = (theme: Theme): CSSObject => ({
    marginTop: SIDEBAR_MARGIN,
    width: `calc(${theme.spacing(7)} + 1px)`,
    transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: "hidden",
    [theme.breakpoints.up("sm")]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },

    "& .MuiListSubheader-root, .MuiListItemText-root": {
        display: "none",
    },

    "& .MuiButtonBase-root": {
        minHeight: "54px",
        padding: "unset",

        "& .MuiListItemIcon-root": {
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",

            "& svg": {
                width: "34px",
                height: "24px",
            },
        },
    },
});

export const Styled = {
    Sidebar: styled(MuiDrawer, { shouldForwardProp: prop => prop !== "open" })(({ theme }) => ({
        "& .MuiPaper-root": {
            borderRadius: "unset",
            border: "unset",
            borderRight: `1px solid ${theme.palette.secondary.main}`,
            background: theme.palette.secondary.dark,
        },
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        whiteSpace: "nowrap",
        boxSizing: "border-box",
        variants: [
            {
                props: ({ open }) => open,
                style: {
                    ...openedMixin(theme),
                    "& .MuiDrawer-paper": openedMixin(theme),
                },
            },
            {
                props: ({ open }) => !open,
                style: {
                    ...closedMixin(theme),
                    "& .MuiDrawer-paper": closedMixin(theme),
                },
            },
        ],
    })),
    Header: styled("div", {
        shouldForwardProp: prop => prop !== "isOpen",
    })<HeaderProps>`
        display: flex;
        align-items: center;
        justify-content: ${props => (props.isOpen ? "flex-end" : "center")};
        height: 54px;

        & .MuiBox-root {
            &:hover {
                background: none;
            }
        }

        & > div,
        & .MuiButtonBase-root {
            width: 100%;
            max-height: 24px;
            margin-bottom: unset;
            padding: unset;
            &:hover {
                background: none;
            }
        }
    `,
    List: styled(List)`
        & .MuiListSubheader-root {
            background: unset;
            padding: unset;
            font-size: 14px;
            font-weight: 700;
            line-height: 19.6px;
            color: white;
            padding: 8px 0px 8px 4px;

            &:first-of-type {
                padding-top: unset;
            }

            &:not(:first-of-type) {
                border-top: 1px solid ${props => props.theme.palette.secondary.main};
            }
        }
    `,
    ListButton: styled(ListItemButton, {
        shouldForwardProp: prop => prop !== "isActive",
    })<ListButtonProps>`
        && {
            color: ${props => (props.isActive ? "#F9F9F9" : "#929292")};
            background: ${props => (props.isActive ? props.theme.palette.primary.main : "unset")};
        }

        transition:
            background 0.5s ease,
            color 0.5s ease;

        & .MuiListItemIcon-root {
            & svg {
                fill: ${props => (props.isActive ? "#F9F9F9" : "#929292")};
                transition: fill 0.2s ease-in;
            }
        }

        &:hover {
            && {
                background: ${props => props.theme.palette.primary.dark};
            }
            color: #f9f9f9;

            & .MuiListItemIcon-root {
                & svg {
                    fill: #f9f9f9;
                }
            }
        }
    `,
};
